import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeIndianRupee, CheckCircle2, Loader2, PackageCheck, Pencil, Play, XCircle } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dateLocale, withClock } from "@/lib/dateTime";
import { formatINR } from "@/lib/utils";
import { OrderStatusBadge, ORDER_STATUS_LABELS } from "./OrderStatusBadge";
import { OrderDeliveries } from "./OrderDeliveries";
import type { OrderRecord, OrderStatus } from "./orderTypes";

const formatDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";
const formatWhen = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));

const ACTION_LABELS: Record<string, string> = {
  CREATED: "Order created",
  UPDATED: "Details changed",
  CONFIRMED: "Order confirmed",
  STARTED: "Work started",
  DELIVERED: "Delivered",
  CANCELLED: "Order cancelled",
  PAID: "Payment received",
  DISPATCHED: "Items sent",
  DELIVERY_ARRIVED: "Delivery arrived",
  DELIVERY_CANCELLED: "Delivery cancelled",
};

export function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [order, setOrder] = useState<OrderRecord | null>(null);
  useRecordCrumb(order?.order_number);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ customer_po_number: "", customer_po_date: "", delivery_date: "", delivery_address: "", terms: "", notes: "", payment_term_id: "", payment_terms_reason: "" });
  const [paymentTerms, setPaymentTerms] = useState<{ id: string; label: string }[]>([]);
  const [creditReason, setCreditReason] = useState("");
  const [paidOpen, setPaidOpen] = useState(false);
  const [hasDeliveries, setHasDeliveries] = useState(false);
  const [payRef, setPayRef] = useState("");
  useEffect(() => {
    api.get("/api/PaymentTerm/List").then((r) => setPaymentTerms(unwrapArray(r))).catch(() => setPaymentTerms([]));
  }, []);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [noPoReason, setNoPoReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = async () => {
    if (!id) return;
    try {
      setOrder(unwrapObject<OrderRecord>(await api.get(`/api/SalesOrder/ById/${id}`)));
    } catch (e: any) {
      error("Could not open the order", e?.message || "Order not found");
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const run = async (fn: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      await fn();
      success(done, "");
      await load();
      return true;
    } catch (e: any) {
      error("Could not do that", e?.message || "Try again");
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loading || !order) return <FormSkeleton />;

  const status = order.order_status;
  const next = order.allowed_next || [];
  const closed = status === "DELIVERED" || status === "CANCELLED";
  const canEdit = hasPermission("order.update") && !closed;
  const can = (to: OrderStatus) => next.includes(to);
  const overLimit = status === "DRAFT" && !!order.credit && order.credit.over_by > 0;
  const canOverrideCredit = hasPermission("order.credit_override");

  const openEdit = () => {
    setForm({
      customer_po_number: order.customer_po_number || "",
      customer_po_date: order.customer_po_date?.slice(0, 10) || "",
      delivery_date: order.delivery_date?.slice(0, 10) || "",
      delivery_address: order.delivery_address || "",
      terms: order.terms || "",
      notes: order.notes || "",
      payment_term_id: order.payment_term_id || "",
      payment_terms_reason: order.payment_terms_reason || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(
      () =>
        api.put(`/api/SalesOrder/Update/${order.id}`, {
          customer_po_number: form.customer_po_number.trim(),
          customer_po_date: form.customer_po_date || undefined,
          delivery_date: form.delivery_date || undefined,
          delivery_address: form.delivery_address.trim(),
          terms: form.terms.trim(),
          notes: form.notes.trim(),
          payment_term_id: form.payment_term_id || undefined,
          payment_terms_reason: form.payment_terms_reason.trim() || undefined,
        }),
      "Order updated",
    );
    if (ok) setEditOpen(false);
  };

  // A signed agreement still in force stands in for the customer's PO
  const coveredByAgreement = !!order.agreement && order.agreement.agreement_status === "EXECUTED" && !!order.agreement.end_date && order.agreement.end_date.slice(0, 10) >= new Date().toISOString().slice(0, 10);

  const doConfirm = async () => {
    const ok = await run(
      () => api.patch(`/api/SalesOrder/Confirm/${order.id}`, { no_po_reason: noPoReason.trim() || undefined, credit_override_reason: creditReason.trim() || undefined }),
      "Order confirmed",
    );
    if (ok) {
      setConfirmOpen(false);
      setNoPoReason("");
      setCreditReason("");
    }
  };

  const doPaid = async () => {
    const ok = await run(() => api.patch(`/api/SalesOrder/Paid/${order.id}`, { reference: payRef.trim() || undefined }), "Payment recorded");
    if (ok) {
      setPaidOpen(false);
      setPayRef("");
    }
  };

  const doCancel = async () => {
    const ok = await run(() => api.patch(`/api/SalesOrder/Cancel/${order.id}`, { reason: cancelReason.trim() }), "Order cancelled");
    if (ok) {
      setCancelOpen(false);
      setCancelReason("");
    }
  };

  const line = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between gap-4 text-[13px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4 pb-10">
      <section aria-label="Order summary" className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="space-y-1">
            <Link to="/orders" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="size-3.5" /> All orders
            </Link>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-[19px] font-semibold leading-tight tracking-tight font-mono">{order.order_number}</h1>
              <OrderStatusBadge status={status} />
            </div>
            <p className="text-[13px] text-muted-foreground">
              <Link to={`/accounts/${order.account_id}`} className="font-medium text-foreground hover:underline">
                {order.account?.name}
              </Link>{" "}
              · Deal{" "}
              <Link to={`/deals/${order.deal_id}`} className="text-primary hover:underline">
                {order.deal?.name}
              </Link>
              {order.quotation && (
                <>
                  {" "}
                  · Quote{" "}
                  <Link to={`/quotes/${order.quotation.id}`} className="text-primary hover:underline font-mono">
                    {order.quotation.display_number}
                  </Link>
                </>
              )}
              {order.agreement && (
                <>
                  {" "}
                  · Agreement{" "}
                  <Link to={`/agreements/${order.agreement.id}`} className="text-primary hover:underline font-mono">
                    {order.agreement.display_number}
                  </Link>
                </>
              )}
              {order.owner && ` · owner ${order.owner.first_name} ${order.owner.last_name || ""}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" onClick={openEdit} disabled={busy}>
                <Pencil className="size-4 mr-1.5" /> Edit details
              </Button>
            )}
            {can("CONFIRMED") && hasPermission("order.confirm") && (
              <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
                <CheckCircle2 className="size-4 mr-1.5" /> Confirm order
              </Button>
            )}
            {status === "CONFIRMED" && !hasDeliveries && hasPermission("order.update") && (
              <Button variant="outline" size="sm" onClick={() => run(() => api.patch(`/api/SalesOrder/Start/${order.id}`), "Work started")} disabled={busy}>
                <Play className="size-4 mr-1.5" /> Start work
              </Button>
            )}
            {can("DELIVERED") && !hasDeliveries && hasPermission("order.update") && (
              <Button variant="outline" size="sm" onClick={() => run(() => api.patch(`/api/SalesOrder/Deliver/${order.id}`), "Marked as delivered")} disabled={busy}>
                <PackageCheck className="size-4 mr-1.5" /> Mark delivered
              </Button>
            )}
            {status === "DELIVERED" && !order.paid_at && hasPermission("order.update") && (
              <Button variant="outline" size="sm" onClick={() => setPaidOpen(true)} disabled={busy}>
                <BadgeIndianRupee className="size-4 mr-1.5" /> Record payment
              </Button>
            )}
            {can("CANCELLED") && !hasDeliveries && hasPermission("order.cancel") && (
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => setCancelOpen(true)} disabled={busy}>
                <XCircle className="size-4 mr-1.5" /> Cancel order
              </Button>
            )}
          </div>
        </div>
      </section>

      {status === "CANCELLED" && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
          Cancelled on {formatDate(order.cancelled_at)}: {order.cancel_reason}
        </div>
      )}
      {status === "DRAFT" && (
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
          This order is a draft. Add the customer's PO number, then a Sales Head confirms it.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Disc %</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(order.lines || []).map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      <div className="text-sm font-medium">{l.product_name}{l.variant_label ? ` · ${l.variant_label}` : ""}</div>
                      {l.spec && <div className="text-[11px] text-muted-foreground">{l.spec}</div>}
                      <div className="text-[11px] text-muted-foreground">{[l.product_code, l.hsn_sac_code && `HSN ${l.hsn_sac_code}`].filter(Boolean).join(" · ")}</div>
                    </TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">{Number(l.quantity)} {l.unit}</TableCell>
                    <TableCell className="text-right text-sm">{formatINR(l.unit_price)}</TableCell>
                    <TableCell className="text-right text-sm">{Number(l.discount_pct)}</TableCell>
                    <TableCell className="text-right text-sm">{formatINR(l.taxable_amount)}</TableCell>
                    <TableCell className="text-right text-sm">{Number(l.gst_rate)}%</TableCell>
                    <TableCell className="text-right text-sm font-semibold">{formatINR(l.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="ml-auto max-w-xs space-y-1.5 border-t p-4">
              {line("Subtotal", formatINR(order.subtotal))}
              {Number(order.discount_total) > 0 && line("Discount", `− ${formatINR(order.discount_total)}`)}
              {line("Taxable value", formatINR(order.taxable_total))}
              {order.gst_type === "INTER" ? line("IGST", formatINR(order.igst_total)) : <>{line("CGST", formatINR(order.cgst_total))}{line("SGST", formatINR(order.sgst_total))}</>}
              {Number(order.round_off) !== 0 && line("Round off", formatINR(order.round_off))}
              <div className="flex justify-between border-t pt-2 text-sm font-semibold"><span>Grand total</span><span>{formatINR(order.grand_total)}</span></div>
            </div>
          </div>
          <OrderDeliveries orderId={order.id} status={status} canEdit={hasPermission("order.update")} onChanged={load} onLoaded={setHasDeliveries} />
          {order.terms && (
            <div className="rounded-lg border border-border bg-card p-4 text-[13px]">
              <div className="mb-1 font-semibold">Terms</div>
              <p className="whitespace-pre-wrap text-muted-foreground">{order.terms}</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 space-y-2">
            <div className="text-sm font-semibold">Order details</div>
            {line("Order date", formatDate(order.order_date))}
            {line("Customer PO", order.customer_po_number || <span className="text-warning">{order.no_po_reason ? `None: ${order.no_po_reason}` : coveredByAgreement ? "None (covered by the agreement)" : "Not entered"}</span>)}
            {line("PO date", formatDate(order.customer_po_date))}
            {line("Delivery date", formatDate(order.delivery_date))}
            {line("Payment terms", order.payment_terms || "-")}
            {order.payment_terms_reason && <div className="text-[12px] text-muted-foreground">Different from usual: {order.payment_terms_reason}</div>}
            {status === "DELIVERED" && line("Payment", order.paid_at ? `Received ${formatDate(order.paid_at)}${order.payment_reference ? ` · ${order.payment_reference}` : ""}` : <span className="text-warning">Not received yet</span>)}
            {line("Place of supply", order.place_of_supply || "-")}
            {order.delivery_address && <div className="pt-1 text-[13px]"><div className="text-muted-foreground">Deliver to</div><p className="whitespace-pre-wrap">{order.delivery_address}</p></div>}
            {order.notes && <div className="pt-1 text-[13px]"><div className="text-muted-foreground">Notes</div><p className="whitespace-pre-wrap">{order.notes}</p></div>}
          </div>

          {order.credit && order.credit.limit !== null && (
            <div className={`rounded-lg border p-4 space-y-2 ${order.credit.over_by > 0 && !closed ? "border-warning/50 bg-warning/10" : "border-border bg-card"}`}>
              <div className="text-sm font-semibold">Customer credit</div>
              {line("Credit limit", formatINR(order.credit.limit))}
              {line("Open orders (unpaid)", formatINR(order.credit.open_orders_value))}
              {status === "DRAFT" && line("With this order", formatINR(order.credit.total_with_this_order))}
              {status === "DRAFT" && order.credit.over_by > 0 && <div className="text-[13px] font-medium text-warning">{formatINR(order.credit.over_by)} over the limit. Confirming needs a Super Admin{order.credit_approval_requested_at ? " (already asked)" : ""}.</div>}
              {order.credit_override_reason && <div className="text-[12px] text-muted-foreground">Confirmed over the limit: {order.credit_override_reason}</div>}
            </div>
          )}

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-3 text-sm font-semibold">History</div>
            <ol className="space-y-3">
              {(order.history || []).map((h) => (
                <li key={h.id} className="text-[13px]">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{ACTION_LABELS[h.action] || h.action}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formatWhen(h.created_on)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[h.by, h.from_status && h.to_status && h.from_status !== h.to_status ? `${ORDER_STATUS_LABELS[h.from_status as OrderStatus] || h.from_status} → ${ORDER_STATUS_LABELS[h.to_status as OrderStatus] || h.to_status}` : null, h.comment].filter(Boolean).join(" · ")}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit order details</DialogTitle>
            <DialogDescription>The items and prices stay as they were agreed in the quote.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Customer PO number</Label><Input value={form.customer_po_number} onChange={(e) => setForm({ ...form, customer_po_number: e.target.value })} maxLength={100} /></div>
              <div className="space-y-1.5"><Label>PO date</Label><Input type="date" value={form.customer_po_date} onChange={(e) => setForm({ ...form, customer_po_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Delivery date</Label><Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Delivery address</Label><Textarea rows={2} value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} maxLength={1000} /></div>
            <div className="space-y-1.5">
              <Label>Payment terms</Label>
              <SearchableSelect value={form.payment_term_id} onChange={(e) => setForm({ ...form, payment_term_id: e.target.value })} className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm">
                {!form.payment_term_id && <option value="">-- None --</option>}
                {paymentTerms.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                {form.payment_term_id && !paymentTerms.some((t) => t.id === form.payment_term_id) && <option value={form.payment_term_id}>{order.payment_terms || "Earlier term"}</option>}
              </SearchableSelect>
              {form.payment_term_id !== (order.payment_term_id || "") && <Input placeholder="Why different from the customer's usual terms? (needed if it is)" value={form.payment_terms_reason} onChange={(e) => setForm({ ...form, payment_terms_reason: e.target.value })} maxLength={500} />}
            </div>
            <div className="space-y-1.5"><Label>Terms</Label><Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} maxLength={2000} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={2000} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm order {order.order_number}</DialogTitle>
            <DialogDescription>
              {order.customer_po_number
                ? `Customer PO ${order.customer_po_number} is on the order.`
                : coveredByAgreement
                  ? `There is no customer PO number, but signed agreement ${order.agreement!.display_number} is in force, so it covers this order. You can add a note if you like.`
                  : "There is no customer PO number on this order. Say why you are confirming without one."}
            </DialogDescription>
          </DialogHeader>
          {!order.customer_po_number && (
            <div className="space-y-1.5">
              <Label>{coveredByAgreement ? "Note (optional)" : "Reason for no PO *"}</Label>
              <Textarea rows={2} value={noPoReason} onChange={(e) => setNoPoReason(e.target.value)} placeholder="e.g. Confirmed by email, PO to follow" maxLength={500} />
            </div>
          )}
          {overLimit && (
            <div className="space-y-1.5 rounded-md border border-warning/50 bg-warning/10 p-3 text-[13px]">
              <div className="font-medium">
                This takes the customer {formatINR(order.credit!.over_by)} over their credit limit ({formatINR(order.credit!.total_with_this_order)} against {formatINR(order.credit!.limit as number)}).
              </div>
              {canOverrideCredit ? (
                <>
                  <Label>Why confirm anyway? *</Label>
                  <Textarea rows={2} value={creditReason} onChange={(e) => setCreditReason(e.target.value)} maxLength={500} />
                </>
              ) : (
                <div className="text-muted-foreground">Only a Super Admin can confirm it. Pressing Confirm asks them to approve.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Not yet</Button>
            <Button onClick={doConfirm} disabled={busy || (!order.customer_po_number && !coveredByAgreement && !noPoReason.trim()) || (overLimit && canOverrideCredit && !creditReason.trim())}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}{overLimit && !canOverrideCredit ? "Ask a Super Admin" : "Confirm order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paidOpen} onOpenChange={setPaidOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record payment for {order.order_number}</DialogTitle>
            <DialogDescription>It stops counting against the customer's credit limit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reference (UTR, cheque number, receipt)</Label>
            <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} maxLength={200} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidOpen(false)}>Cancel</Button>
            <Button onClick={doPaid} disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Record payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel order {order.order_number}</DialogTitle>
            <DialogDescription>The owner is told. A cancelled order frees its quote for a new order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={500} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep the order</Button>
            <Button variant="destructive" onClick={doCancel} disabled={busy || !cancelReason.trim()}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Cancel order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
